/** Display formatting (UI_UX_GUIDELINES "Money, quantities, dates"). The console shows instants in
 * the admin's own time zone; shops' business dates are calendar dates (no shift). */

export interface FormatSettings {
  currencyCode: string;
  currencyDecimals: number;
  locale: string;
  timeZone: string;
}

export const DEFAULT_FORMAT: FormatSettings = {
  currencyCode: 'PKR',
  currencyDecimals: 2,
  locale: 'en-PK',
  timeZone: browserTimeZone(),
};

function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();
function memo<T extends Intl.NumberFormat | Intl.DateTimeFormat>(key: string, make: () => T): T {
  let f = cache.get(key) as T | undefined;
  if (!f) {
    f = make();
    cache.set(key, f);
  }
  return f;
}

function safeLocale(locale: string): string {
  try {
    return Intl.getCanonicalLocales(locale)[0] ?? 'en';
  } catch {
    return 'en';
  }
}

/** `Rs 1,250.00` — amounts arrive from the API as decimal strings. */
export function money(
  value: string | number | null | undefined,
  s: FormatSettings = DEFAULT_FORMAT,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  const f = memo(
    `m:${s.locale}:${s.currencyCode}:${String(s.currencyDecimals)}`,
    () =>
      new Intl.NumberFormat(safeLocale(s.locale), {
        style: 'currency',
        currency: s.currencyCode,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: s.currencyDecimals,
        maximumFractionDigits: s.currencyDecimals,
      }),
  );
  return f.format(n);
}

/** Compact money for KPI tiles: `Rs 1.2M`. */
export function moneyCompact(
  value: string | number | null | undefined,
  s: FormatSettings = DEFAULT_FORMAT,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const f = memo(
    `mc:${s.locale}:${s.currencyCode}`,
    () =>
      new Intl.NumberFormat(safeLocale(s.locale), {
        style: 'currency',
        currency: s.currencyCode,
        currencyDisplay: 'narrowSymbol',
        notation: 'compact',
        maximumFractionDigits: 1,
      }),
  );
  return f.format(n);
}

/** Quantities: up to 4 decimals, trailing zeros dropped (`12`, `2.5`). */
export function qty(
  value: string | number | null | undefined,
  s: FormatSettings = DEFAULT_FORMAT,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return memo(
    `q:${s.locale}`,
    () => new Intl.NumberFormat(safeLocale(s.locale), { maximumFractionDigits: 4 }),
  ).format(n);
}

/** Business dates (YYYY-MM-DD) are calendar dates: formatted without a time-zone shift. */
export function businessDate(
  value: string | null | undefined,
  s: FormatSettings = DEFAULT_FORMAT,
): string {
  if (!value) return '—';
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return value;
  return memo(
    `d:${s.locale}`,
    () =>
      new Intl.DateTimeFormat(safeLocale(s.locale), {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }),
  ).format(new Date(Date.UTC(y, m - 1, d)));
}

/** Instants (ISO timestamps) in the shop's time zone. */
export function dateTime(
  value: string | null | undefined,
  s: FormatSettings = DEFAULT_FORMAT,
): string {
  if (!value) return '—';
  return memo(
    `dt:${s.locale}:${s.timeZone}`,
    () =>
      new Intl.DateTimeFormat(safeLocale(s.locale), {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: s.timeZone,
      }),
  ).format(new Date(value));
}

/** "3 min ago", "yesterday" — for notifications and activity. */
export function relativeTime(value: string, now = Date.now()): string {
  const diff = (new Date(value).getTime() - now) / 1000;
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  for (const [unit, secs] of units)
    if (Math.abs(diff) >= secs) return rtf.format(Math.round(diff / secs), unit);
  return 'just now';
}

/** Initials for avatars: "Ahmed Builders" → "AB". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase() ||
    '?'
  );
}

/** Whole counts with grouping: `12,480`. */
export function count(
  value: number | null | undefined,
  s: FormatSettings = DEFAULT_FORMAT,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return memo(`c:${s.locale}`, () => new Intl.NumberFormat(safeLocale(s.locale))).format(value);
}

/** An instant as a calendar date in the admin's time zone: `21 Oct 2026`. */
export function date(value: string | null | undefined, s: FormatSettings = DEFAULT_FORMAT): string {
  if (!value) return '—';
  return memo(
    `da:${s.locale}:${s.timeZone}`,
    () =>
      new Intl.DateTimeFormat(safeLocale(s.locale), {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: s.timeZone,
      }),
  ).format(new Date(value));
}

/** Byte sizes: `1.4 GB`. */
export function bytes(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = value;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${i === 0 ? String(n) : n.toFixed(n < 10 ? 1 : 0)} ${units[i] ?? 'B'}`;
}

/** Durations from seconds: `3d 4h`, `5h 12m`, `42s`. */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3_600);
  const m = Math.floor((s % 3_600) / 60);
  if (d > 0) return `${String(d)}d ${String(h)}h`;
  if (h > 0) return `${String(h)}h ${String(m)}m`;
  if (m > 0) return `${String(m)}m`;
  return `${String(s)}s`;
}

/** "1 tenant", "3 tenants". */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${count(n)} ${n === 1 ? one : many}`;
}
